import * as React from 'react'
import { Text, getValidity, Select, Toggle } from '../../../../form'
import { FormattedMessage, IntlShape } from 'react-intl'
import messages from '../../messages'
import styles from './inputs.module.scss'
import { validatePhoneNumber, normalizePhoneNumber } from '../../../../../lib/utils'
import ApolloErrorMessage from '../../../../apollo-error-message'
import Button from '../../../../button'
import { ApolloError } from 'apollo-client'
import Error from '../../../../error'
import { companyBranches, companyBanks } from '../../../../../lib/signup-utils'
import { ContactFormVariant, IFormValues } from '../../types'
import { FormState, Inputs as IInputs } from 'react-use-form-state'
import FindCompany, { IFindCompanyCompany } from '../../../../find-company'
import { CheckIcon } from '../../../../icon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons'

interface IInputsProps {
  error?: ApolloError
  loading?: boolean
  variant?: ContactFormVariant
  formState: FormState<IFormValues>
  input: IInputs<IFormValues>
  intl: IntlShape
  company?: IFindCompanyCompany | null
  setCompany?: (newCompany: IFindCompanyCompany | null) => void
}

const Inputs: React.FC<IInputsProps> = ({ error, loading, formState, input, intl, variant, setCompany, company }) => {
  return (
    <div className={styles.inputs}>
      <CompanyInputs
        company={company}
        formState={formState}
        input={input}
        intl={intl}
        setCompany={setCompany}
        variant={variant}
      />
      <ContactinformationInputs formState={formState} input={input} intl={intl} variant={variant} />

      <Toggle
        checked={Boolean(formState.values.agreements)}
        errorMessage={intl.formatMessage(messages.errorAgreements)}
        input={input}
        label={intl.formatMessage(messages.labelAgreements, {
          link1: (...chunks: React.ReactElement[] | string[]) => (
            <a href="/gdpr" rel="noopener noreferrer" target="_blank">
              {chunks}
            </a>
          ),
        })}
        name="agreements"
        required
        valid={getValidity(formState, 'agreements')}
        validate={(value: string) => value}
        validateOnBlur
        variant="checkbox"
      />

      {error && (
        <Error>
          <ApolloErrorMessage error={error} />
        </Error>
      )}

      <Button block loading={loading} type="submit" variant="success">
        <FormattedMessage {...messages.cta} />
      </Button>
    </div>
  )
}

const CompanyInputs: React.FC<IInputsProps> = ({ input, variant, formState, intl, company, setCompany }) => (
  <>
    {variant !== 'regular' && <FormattedMessage {...messages.companyInfoHeading} tagName="h4" />}

    {variant === 'regular' ? (
      <Text
        errorCollapse="dynamic"
        hideIcon
        input={input}
        name="companyName"
        placeholder={intl.formatMessage(messages.labelCompanyName)}
        type="text"
      />
    ) : (
      <>
        <FindCompany
          hideIcon
          onlyShowInput
          onSubmit={(newCompany: IFindCompanyCompany) => setCompany && setCompany(newCompany)}
          placeholder={intl.formatMessage(messages.labelFindCompany)}
        />

        {company && (
          <div className={styles.company}>
            <div className={styles.left}>
              <CheckIcon theme="success" />
              <h5>{`${company.name}, ${company.displayOrgNr}`}</h5>
            </div>
            <FontAwesomeIcon
              className={styles.closeIcon}
              icon={faTimesCircle}
              onClick={() => setCompany && setCompany(null)}
            />
          </div>
        )}
      </>
    )}

    {variant === 'regular' && (
      <Select
        errorCollapse="dynamic"
        input={input}
        name="companyStatus"
        options={[
          {
            text: intl.formatMessage(messages.optionCompanyStatusDefault),
            selected: formState.values.companyStatus === 'default',
            value: 'default',
          },
          {
            text: intl.formatMessage(messages.optionCompanyStatusNotStarted),
            selected: formState.values.companyStatus === 'notStarted',
            value: 'notStarted',
          },
          {
            text: intl.formatMessage(messages.optionCompanyStatusNew),
            selected: formState.values.companyStatus === 'new',
            value: 'new',
          },
          {
            text: intl.formatMessage(messages.optionCompanyStatusActive),
            selected: formState.values.companyStatus === 'active',
            value: 'active',
          },
        ]}
        validateOnBlur
      />
    )}

    <div className={styles.horizontal}>
      {variant !== 'regular' && (
        <Select
          errorCollapse="dynamic"
          input={input}
          name="companyBranch"
          options={[
            {
              text: intl.formatMessage(messages.optionCompanyBranchDefault),
              selected: !formState.values.companyBranch,
              value: '',
            },
            ...Object.keys(companyBranches).map((branchId) => ({
              text: intl.formatMessage(companyBranches[branchId]),
              selected: branchId === formState.values.companyBranch,
              value: branchId,
            })),
          ]}
          required
          validateOnBlur
        />
      )}

      {variant === 'regular' ? (
        <Select
          errorCollapse="dynamic"
          input={input}
          name="companyBranch"
          options={[
            {
              text: intl.formatMessage(messages.optionCompanyBranchDefault),
              selected: !formState.values.companyBranch,
              value: '',
            },
            ...Object.keys(companyBranches).map((branchId) => ({
              text: intl.formatMessage(companyBranches[branchId]),
              selected: branchId === formState.values.companyBranch,
              value: branchId,
            })),
          ]}
          required
          validateOnBlur
        />
      ) : (
        <Select
          errorCollapse="dynamic"
          input={input}
          name="bank"
          options={[
            {
              text: intl.formatMessage(messages.optionCompanyBankDefault),
              selected: !formState.values.bank,
              value: '',
            },
            ...Object.keys(companyBanks).map((bankId) => ({
              text: companyBanks[bankId],
              selected: bankId === formState.values.bank,
              value: bankId,
            })),
            {
              text: intl.formatMessage(messages.optionCompanyBankOther),
              selected: formState.values.bank === 'other',
              value: 'other',
            },
          ]}
          required
          validateOnBlur
        />
      )}
    </div>

    {variant === 'companyOld' && (
      <div className={styles.horizontal}>
        <Text
          errorCollapse="dynamic"
          hideIcon
          input={input}
          name="companyEmployees"
          placeholder={intl.formatMessage(messages.labelCompanyEmployees)}
          type="text"
        />
        <Select
          errorCollapse="dynamic"
          input={input}
          name="companyRevenue"
          options={[
            {
              text: intl.formatMessage(messages.optionCompanyRevenue),
              selected: !formState.values.companyRevenue,
              value: '',
            },
            {
              text: intl.formatMessage(messages.optionCompanyRevenue0),
              selected: formState.values.companyRevenue === '0-1',
              value: '0-1',
            },
            {
              text: intl.formatMessage(messages.optionCompanyRevenue1),
              selected: formState.values.companyRevenue === '1-3',
              value: '1-3',
            },
            {
              text: intl.formatMessage(messages.optionCompanyRevenue3),
              selected: formState.values.companyRevenue === '3-5',
              value: '3-5',
            },
            {
              text: intl.formatMessage(messages.optionCompanyRevenue5),
              selected: formState.values.companyRevenue === '5',
              value: '5',
            },
          ]}
          validateOnBlur
        />
      </div>
    )}
  </>
)

const ContactinformationInputs: React.FC<IInputsProps> = ({ input, variant, formState, intl }) => (
  <>
    {variant !== 'regular' && <FormattedMessage {...messages.contactInfoHeading} tagName="h4" />}
    <Text
      errorCollapse="dynamic"
      errorMessage={intl.formatMessage(messages.errorName)}
      hideIcon
      input={input}
      name="name"
      placeholder={intl.formatMessage(messages.labelName)}
      required
      type="text"
      valid={getValidity(formState, 'name')}
      validate={(value: string) => value.length > 0 && value.length < 256}
      validateOnBlur
    />

    <div className={styles.horizontal}>
      <Text
        errorCollapse="dynamic"
        errorMessage={intl.formatMessage(messages.errorEmail)}
        hideIcon
        input={input}
        name="email"
        placeholder={intl.formatMessage(messages.labelEmail)}
        required
        type="email"
        valid={getValidity(formState, 'email')}
        validate={(value: string) => value.length > 0 && value.length < 256}
        validateOnBlur
      />

      <Text
        errorCollapse="dynamic"
        errorMessage={intl.formatMessage(messages.errorPhone)}
        hideIcon
        input={input}
        name="phone"
        placeholder={intl.formatMessage(messages.labelPhone)}
        required
        type="tel"
        valid={getValidity(formState, 'phone')}
        validate={(value: string) =>
          validatePhoneNumber(normalizePhoneNumber(value)) ? true : intl.formatMessage(messages.errorPhone)
        }
        validateOnBlur
      />
    </div>

    <Text
      errorCollapse="dynamic"
      hideIcon
      input={input}
      name="description"
      placeholder={intl.formatMessage(messages.labelDescription)}
      textArea
      type="text"
    />
  </>
)

export default Inputs
